import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { IReceptionListPageProps, IVisitor } from './types';
import { Container, Alert } from 'react-bootstrap';
import AjaxErrorBoundary from '../../ajax/errorBoundary';
import { FormHeading } from '../../form/formHeading';
import { MutedText } from '../../form';
import { ReceptionMobileList } from './ReceptionMobileList';
import { ReceptionDesktopList } from './ReceptionDesktopList';
import byTimes from './byTimes';
import attemptVisitorSignOut from '../../visitor/signOut/attemptVisitorSignOut';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const ReceptionListPage: React.FC<IReceptionListPageProps> = ({
  busy,
  errors,
  visitors,
  visitorTypeId,
  tableMode,
  showSignedOutVisitors,
  isToday,
}) => {
  const history = useHistory();

  const [signingOut, setSigningOut] = useState<Array<number>>([]);

  const getFilterVisitors = (
    visitors: Array<IVisitor>,
    visitorTypeId: string,
    showSignedOutVisitors: boolean,
    isToday: boolean,
  ): Array<IVisitor> => {
    let filteredList: Array<IVisitor> =
      showSignedOutVisitors || !isToday
        ? visitors
        : visitors.filter((visitor) => visitor.outAt === null);

    if (visitorTypeId !== '') {
      filteredList = filteredList.filter(
        (visitor) => visitor.visitorType.id.toString() === visitorTypeId,
      );
    }

    return filteredList;
  };

  const filteredVisitors = useMemo(
    () =>
      getFilterVisitors(
        visitors,
        visitorTypeId,
        showSignedOutVisitors,
        isToday,
      ).sort(byTimes),
    [visitors, visitorTypeId, showSignedOutVisitors, isToday],
  );

  const handleClick = (visitorId: number): void =>
    history.push(`/reception/visitor/${visitorId}`);

  const handleSignOut = (visitor: IVisitor): void => {
    setSigningOut([...signingOut, visitor.id]);

    attemptVisitorSignOut(visitor.badgeNumber)
      .catch((error) => console.log(error))
      .finally(() => {
        const index = signingOut.indexOf(visitor.id);
        setSigningOut([
          ...signingOut.slice(0, index),
          ...signingOut.slice(index + 1),
        ]);
      });
  };

  return (
    <AjaxErrorBoundary
      busy={busy}
      errors={errors}
      loadingText="Fetching visitors"
    >
      {!isToday && (
        <Alert variant="warning">
          <FontAwesomeIcon
            className="mr-2"
            icon={['far', 'exclamation-circle']}
          />
          You are viewing historic data, some features are unavailable
        </Alert>
      )}
      {visitorTypeId && (
        <Alert variant="info">
          <FontAwesomeIcon className="mr-2" icon={['far', 'info-circle']} />
          You are viewing a filtered list
        </Alert>
      )}
      {(visitorTypeId || !isToday) && filteredVisitors.length === 0 && (
        <MutedText>No records match your current selection(s)</MutedText>
      )}
      {tableMode ? (
        <ReceptionDesktopList
          visitors={filteredVisitors}
          signingOut={signingOut}
          onClick={handleClick}
          onSignOut={handleSignOut}
        />
      ) : (
        <ReceptionMobileList
          visitors={filteredVisitors}
          signingOut={signingOut}
          onClick={handleClick}
          onSignOut={handleSignOut}
        />
      )}
    </AjaxErrorBoundary>
  );
};
